import React, { useEffect, useMemo, useState } from "react";
import { BookOpen, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ListItemCardCst from "@components/ListItemCardCst";
import AddItemsModalCst, {
  AddItemsConfirmEntry,
  AddItemsTabConfig,
} from "@components/AddItemsModalCst";
// import { ACTIVITY_TYPES } from "@pages/Contents/V2/Corporate/Catalog/AddActivity/config/activityTypes";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { useLOSearch } from "@pages/Contents/V2/Corporate/Catalog/search/useLOSearch";
import { useLOFilterableFields } from "@pages/Contents/V2/Corporate/Catalog/search/useLOFilterableFields";
import SearchFiltersToggle from "@pages/Contents/V2/Corporate/Catalog/search/SearchFiltersToggle";
import {
  buildSearchConditions,
  LO_SORT_OPTION_KEYS,
  LO_SORT_ORDER_BY,
  SEARCH_FILTER_ORDER_BY_TAB,
} from "@pages/Contents/V2/Corporate/Catalog/search/types";

export type AddLOKind = Extract<CatalogEntityType, "activity" | "course">;

export interface AddLOSelectedItem {
  id: number;
  title: string;
  type: string;
  kind: AddLOKind;
  entity: Record<string, any>;
}

interface AddLOToPathModalProps {
  open: boolean;
  onClose: () => void;
  excludeIds: number[];
  initialSelectedItems?: AddLOSelectedItem[];
  contentBeforeSearch?: React.ReactNode;
  title?: string;
  maxSelection?: number;
  validateConfirm?: () => boolean;
  onConfirm: (added: AddLOSelectedItem[]) => void;
  onCreate?: (tabKey: AddLOKind) => void;
}

const AddLOToPathModal: React.FC<AddLOToPathModalProps> = ({
  open,
  onClose,
  excludeIds,
  initialSelectedItems = [],
  contentBeforeSearch,
  title,
  maxSelection,
  validateConfirm,
  onConfirm,
  onCreate,
}) => {
  const { t } = useTranslation("add-items-modal");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { id: corporateId = "" } = useParams();

  const [activityType, setActivityType] = useState("");
  const [activityAccordionFilters, setActivityAccordionFilters] = useState<
    Record<string, any>
  >({});
  const [courseAccordionFilters, setCourseAccordionFilters] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    if (!open) return;
    setActivityType("");
    setActivityAccordionFilters({});
    setCourseAccordionFilters({});
  }, [open]);

  const { fields: filterableFields } = useLOFilterableFields(corporateId, open);

  const sortOptions = useMemo(
    () =>
      LO_SORT_OPTION_KEYS.map((key) => ({
        value: key,
        label: String(tCatalog(`sort.${key}`)),
      })),
    [tCatalog],
  );

  // TODO: opzioni del quick filter tipo attività, commentate insieme al
  // filtro esterno all'accordion.
  // const activityTypeOptions = useMemo(
  //   () => [
  //     { value: "", label: String(t("filters.type.all") ?? "Tutti") },
  //     ...ACTIVITY_TYPES.map((td) => ({
  //       value: td.key,
  //       label: String(tActivity(td.titleI18nKey, { defaultValue: td.key })),
  //     })),
  //   ],
  //   [t, tActivity],
  // );

  const useActivityData = (args: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string | null;
  }) => {
    const conditions = useMemo(
      () =>
        buildSearchConditions({
          contentTypes: ["DIDACTICAL_ACTIVITY"],
          includeDrafts: false,
          fields: filterableFields,
          filterValues: {
            ...activityAccordionFilters,
            ...(activityType ? { activityType: [activityType] } : {}),
          },
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filterableFields, activityAccordionFilters, activityType],
    );
    const result = useLOSearch({
      corporateId,
      conditions,
      searchText: args.search,
      orderBy: LO_SORT_ORDER_BY[args.sort ?? ""] ?? LO_SORT_ORDER_BY["id-desc"],
      page: args.page,
      pageSize: args.pageSize,
      enabled: open && !!corporateId,
    });
    return {
      ...result,
      items: (result.items || []).map((raw: any) => raw.lo),
    };
  };

  const useCourseData = (args: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string | null;
  }) => {
    const conditions = useMemo(
      () =>
        buildSearchConditions({
          contentTypes: ["COURSE"],
          includeDrafts: false,
          fields: filterableFields,
          filterValues: courseAccordionFilters,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filterableFields, courseAccordionFilters],
    );
    const result = useLOSearch({
      corporateId,
      conditions,
      searchText: args.search,
      orderBy: LO_SORT_ORDER_BY[args.sort ?? ""] ?? LO_SORT_ORDER_BY["id-desc"],
      page: args.page,
      pageSize: args.pageSize,
      enabled: open && !!corporateId,
    });
    return {
      ...result,
      items: (result.items || []).map((raw: any) => raw.lo),
    };
  };

  const renderLORow =
    (kind: AddLOKind) =>
    ({ item }: { item: Record<string, any> }) => {
      const cardProps = mapEntityToCard(item, kind, {
        t: tCatalog,
        tActivity,
        showAlternativeSectionCount: true,
        onDetails: () => {
          // TODO: pagina detail v2 non disponibile oggi
          console.info("[add-lo] details — TODO", kind, item.id);
        },
      });
      // pulsante "Dettagli" nascosto nel picker
      return <ListItemCardCst {...cardProps} actions={[]} />;
    };

  const tabs: AddItemsTabConfig<Record<string, any>>[] = [
    {
      key: "activity",
      label: String(t("tabs.activities") ?? "Attività"),
      icon: <GraduationCap size={14} />,
      useDataHook: useActivityData,
      renderRow: renderLORow("activity"),
      getId: (it) => Number(it.id),
      rowVariant: "bare",
      // TODO: quick filter tipo attività rimosso perché duplicato del campo
      // "Tipo attività" nell'accordion dinamico ripristinare se servirà di
      // nuovo la scorciatoia fuori dall'accordion.
      // useFiltersDefs: (): AddItemsTabFilterDef[] => [
      //   {
      //     id: "add-lo-activity-type",
      //     label: String(t("filters.type.label") ?? "Tipo"),
      //     options: activityTypeOptions,
      //     value: activityType,
      //     onChange: setActivityType,
      //   },
      // ],
      renderFiltersAccordion: ({ onApplied }) => (
        <SearchFiltersToggle
          fields={filterableFields}
          fieldOrder={SEARCH_FILTER_ORDER_BY_TAB.activities}
          value={activityAccordionFilters}
          onApply={(next) => {
            setActivityAccordionFilters(next);
            onApplied();
          }}
          columns={3}
        />
      ),
    },
    {
      key: "course",
      label: String(t("tabs.courses") ?? "Corsi"),
      icon: <BookOpen size={14} />,
      useDataHook: useCourseData,
      renderRow: renderLORow("course"),
      getId: (it) => Number(it.id),
      rowVariant: "bare",
      renderFiltersAccordion: ({ onApplied }) => (
        <SearchFiltersToggle
          fields={filterableFields}
          fieldOrder={SEARCH_FILTER_ORDER_BY_TAB.courses}
          value={courseAccordionFilters}
          onApply={(next) => {
            setCourseAccordionFilters(next);
            onApplied();
          }}
          columns={3}
        />
      ),
    },
  ];

  const handleConfirm = (
    entries: AddItemsConfirmEntry<Record<string, any>>[],
  ) => {
    if (validateConfirm && !validateConfirm()) return;
    const added: AddLOSelectedItem[] = entries.map((e) => ({
      id: Number(e.item.id),
      title: e.item.title || e.item.name || `#${e.item.id}`,
      type: e.item.type || e.item.learning_object_type || "",
      kind: e.tabKey as AddLOKind,
      entity: e.item,
    }));
    onConfirm(added);
    onClose();
  };

  const excludeIdsByTab = useMemo(
    () => ({
      activity: excludeIds,
      course: excludeIds,
    }),
    [excludeIds],
  );

  const initialSelectedByTab = useMemo(
    () => ({
      activity: initialSelectedItems
        .filter((item) => item.kind === "activity")
        .map((item) => ({
          ...item.entity,
          id: item.id,
          title: item.title,
          type: item.type,
        })),
      course: initialSelectedItems
        .filter((item) => item.kind === "course")
        .map((item) => ({
          ...item.entity,
          id: item.id,
          title: item.title,
          type: item.type,
        })),
    }),
    [initialSelectedItems],
  );

  return (
    <AddItemsModalCst<Record<string, any>>
      open={open}
      onClose={onClose}
      title={title ?? String(t("title.add-lo") ?? "Aggiungi Elementi")}
      selectionMode="multi"
      tabs={tabs}
      initialTab="activity"
      onConfirm={handleConfirm}
      // onCreate={onCreate ? (tab) => onCreate(tab as AddLOKind) : undefined}
      sortOptions={sortOptions}
      defaultSort="id-desc"
      excludeIdsByTab={excludeIdsByTab}
      initialSelectedByTab={initialSelectedByTab}
      contentBeforeSearch={contentBeforeSearch}
      maxSelection={maxSelection}
      confirmLabel={String(t("confirm") ?? "Aggiungi")}
      // createLabel={String(t("create") ?? "Crea")}
    />
  );
};

export default AddLOToPathModal;
